import { cn } from "cn";
import { Wifi, WifiOff } from "lucide-react";
import type * as React from "react";

/**
 * The app-wide connection pill (v3.2 shell): ink "You're offline" with an
 * amber badge (and, from 640px, `hint`), or a light "Back online". Fixed at
 * the top centre; display only.
 */
export function OfflinePill({
  online,
  label,
  hint,
  className,
  testId = "offline-indicator",
}: {
  online: boolean;
  label: React.ReactNode;
  /** After the offline label on wider screens. */
  hint?: React.ReactNode;
  className?: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <div
      aria-live="polite"
      className={cn(
        "pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top,0px)+10px)] z-[90] flex justify-center px-4 lg:top-4",
        className,
      )}
      data-state={online ? "online" : "offline"}
      data-testid={testId}
      role="status"
    >
      <div
        className={cn(
          "pointer-events-auto flex max-w-full items-center gap-2 rounded-full py-1.5 pl-2.5 pr-3.5 font-v3 text-[13px] font-semibold shadow-v3-pop animate-in fade-in-0 zoom-in-95",
          online ? "bg-v3-panel text-v3-ok-text" : "bg-v3-ink text-v3-on-ink",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "flex size-5 shrink-0 items-center justify-center rounded-full",
            online ? "bg-v3-ok-tint" : "bg-v3-warn text-white",
          )}
        >
          {online ? (
            <Wifi className="size-3" strokeWidth={3} />
          ) : (
            <WifiOff className="size-3" strokeWidth={3} />
          )}
        </span>
        {online ? (
          <span>{label}</span>
        ) : (
          <span className="min-w-0 truncate">
            {label}
            {hint && (
              <span className="font-normal opacity-75 max-sm:hidden">
                {" · "}
                {hint}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
